import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { DashboardShell } from "@/components/DashboardShell";
import { getMyPreferences, updateMyPreferences } from "@/lib/preferences.functions";
import { listTeam, inviteMember, revokeInvite, removeMember } from "@/lib/team.functions";
import { getIntegrations, updateWebhook, sendTestWebhook } from "@/lib/integrations.functions";
import { NuvatrSection } from "@/components/twin/NuvatrSection";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  ArrowRight,
  Bell,
  Bot,
  Copy,
  Crown,
  Lock,
  Plug,
  RefreshCw,
  Save,
  Send,
  Shield,
  ShieldCheck,
  Trash2,
  UserPlus,
  Users,
  Webhook,
} from "lucide-react";
import { useHydratedTranslation } from "@/components/marketing/Layout";

import { buildSeo } from "@/lib/seo";

export const Route = createFileRoute("/_authenticated/settings")({
  head: () =>
    buildSeo({
      path: "/settings",
      title: "Settings — Reality Twin",
      description:
        "Manage your Reality Twin workspace — company profile, preferences, integrations and account settings in one place.",
      noindex: true,
    }),
  component: Page,
});

// Account-level settings only. Anything that describes the company or its Twin
// — profile, logo, verification, service offering, payout details — is managed
// on the AI Twin page, so owners have one place to look.
type Tab = "team" | "integrations" | "notifications" | "security";

const TAB_META: Record<Tab, { label: string; icon: typeof Users }> = {
  team: { label: "Team", icon: Users },
  integrations: { label: "Integrations", icon: Plug },
  notifications: { label: "Notifications", icon: Bell },
  security: { label: "Security", icon: Shield },
};

function Page() {
  const t = useHydratedTranslation();
  const fetchPrefs = useServerFn(getMyPreferences);
  const savePrefs = useServerFn(updateMyPreferences);
  const [prefs, setPrefs] = useState<any>(null);
  const [tab, setTab] = useState<Tab>("team");
  const [password, setPassword] = useState("");

  useEffect(() => {
    fetchPrefs().then((r) => setPrefs(r.notifications));
  }, [fetchPrefs]);

  // Team tab
  const fetchTeam = useServerFn(listTeam);
  const invite = useServerFn(inviteMember);
  const revoke = useServerFn(revokeInvite);
  const remove = useServerFn(removeMember);
  const [team, setTeam] = useState<any>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"member" | "admin">("member");
  const [inviting, setInviting] = useState(false);

  async function loadTeam() {
    try {
      setTeam(await fetchTeam());
    } catch (e: any) {
      toast.error(e.message);
    }
  }
  useEffect(() => {
    if (tab === "team" && !team) loadTeam();
  }, [tab]);

  async function sendInvite(e: React.FormEvent) {
    e.preventDefault();
    setInviting(true);
    try {
      await invite({ data: { email: inviteEmail, role: inviteRole } });
      toast.success(t("app.pages.settings.inviteSent", { email: inviteEmail }));
      setInviteEmail("");
      await loadTeam();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setInviting(false);
    }
  }

  // Integrations tab
  const fetchIntegrations = useServerFn(getIntegrations);
  const saveWebhook = useServerFn(updateWebhook);
  const testWebhook = useServerFn(sendTestWebhook);
  const [integrations, setIntegrations] = useState<any>(null);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [savingHook, setSavingHook] = useState(false);
  const [testingHook, setTestingHook] = useState(false);

  useEffect(() => {
    if (tab !== "integrations" || integrations) return;
    fetchIntegrations()
      .then((r) => {
        setIntegrations(r);
        setWebhookUrl(r.webhookUrl);
      })
      .catch((e: any) => toast.error(e.message));
  }, [tab]);

  async function submitWebhook(regenerateSecret = false) {
    setSavingHook(true);
    try {
      const r = await saveWebhook({ data: { url: webhookUrl.trim(), regenerateSecret } });
      setIntegrations({ ...integrations, ...r });
      toast.success(
        regenerateSecret ? t("app.pages.settings.newSecret") : t("app.pages.settings.webhookSaved"),
      );
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSavingHook(false);
    }
  }

  async function runTestWebhook() {
    setTestingHook(true);
    try {
      const r = await testWebhook();
      toast.success(t("app.pages.settings.testDelivered", { status: r.status ?? 200 }));
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setTestingHook(false);
    }
  }

  async function submitPrefs(e: React.FormEvent) {
    e.preventDefault();
    try {
      await savePrefs({ data: { notifications: prefs } });
      toast.success(t("app.pages.settings.notifsUpdated"));
    } catch (e: any) {
      toast.error(e.message);
    }
  }

  async function updatePassword(e: React.FormEvent) {
    e.preventDefault();
    if (password.length < 8) return toast.error(t("app.pages.settings.pwTooShort"));
    const { error } = await supabase.auth.updateUser({ password });
    if (error) return toast.error(error.message);
    toast.success(t("app.pages.settings.pwUpdated"));
    setPassword("");
  }

  async function signOutAll() {
    const { error } = await supabase.auth.signOut({ scope: "global" });
    if (error) return toast.error(error.message);
    window.location.href = "/auth";
  }

  return (
    <DashboardShell
      title={t("app.pages.settings.title")}
      subtitle={t("app.pages.settings.subtitle")}
    >
      {/* Company profile, verification and payout details moved to the Twin
          page. Say so here so nobody hunts for them. */}
      <Link
        to="/twin"
        className="group mb-5 flex items-start gap-3 rounded-2xl glass border border-border p-4 hover:bg-white/5 transition"
      >
        <div className="h-10 w-10 shrink-0 rounded-xl bg-[oklch(0.82_0.18_168_/_0.12)] border border-[oklch(0.82_0.18_168_/_0.25)] flex items-center justify-center">
          <Bot className="h-4 w-4 text-[oklch(0.82_0.18_168)]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium">
            {t("app.pages.settings.movedTitle", {
              defaultValue: "Company profile, verification and payouts are on your AI Twin",
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t("app.pages.settings.movedSub", {
              defaultValue:
                "Logo, company details, domain verification, service offering and payout account are all managed there.",
            })}
          </p>
        </div>
        <ArrowRight className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground group-hover:text-foreground transition" />
      </Link>

      <div className="flex gap-1.5 mb-6 flex-wrap border-b border-border pb-4">
        {(["team", "integrations", "notifications", "security"] as Tab[]).map((tb) => {
          const meta = TAB_META[tb];
          const Icon = meta.icon;
          const active = tab === tb;
          return (
            <button
              key={tb}
              onClick={() => setTab(tb)}
              className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-xl font-medium transition ${
                active
                  ? "bg-white/10 border border-border text-foreground shadow-sm"
                  : "border border-transparent text-muted-foreground hover:text-foreground hover:bg-white/5"
              }`}
            >
              <Icon
                className={`h-4 w-4 ${active ? "text-[oklch(0.82_0.18_168)]" : ""}`}
                strokeWidth={1.75}
              />
              {t(`app.pages.settings.tab${tb.charAt(0).toUpperCase()}${tb.slice(1)}`)}
            </button>
          );
        })}
      </div>
      {tab === "team" && (
        <div className="space-y-4 max-w-2xl">
          <div className="glass-strong rounded-2xl p-6">
            <div className="flex items-start justify-between gap-3 mb-1">
              <div>
                <div className="font-display text-lg font-semibold">
                  {t("app.pages.settings.teamMembers")}
                </div>
                <p className="text-xs text-muted-foreground mt-1 max-w-md">
                  {t("app.pages.settings.teamSub")}
                </p>
              </div>
              {team && (
                <span className="shrink-0 text-[11px] font-medium px-2.5 py-1 rounded-full bg-white/5 border border-border text-muted-foreground">
                  {t("app.pages.settings.seatsActive", { count: team.members.length + 1 })}
                </span>
              )}
            </div>
            {!team ? (
              <div className="mt-4 space-y-2">
                {[0, 1].map((n) => (
                  <div
                    key={n}
                    className="h-14 rounded-xl border border-border bg-white/[0.02] animate-pulse"
                  />
                ))}
              </div>
            ) : (
              <>
                <div className="mt-4 rounded-xl border border-border overflow-hidden divide-y divide-border">
                  {team.role === "owner" && (
                    <MemberRow email={t("app.pages.settings.you")} role="owner" isYou />
                  )}
                  {team.members.map((m: any) => (
                    <MemberRow
                      key={m.id}
                      email={m.email}
                      role={m.role}
                      onRemove={
                        team.role === "owner"
                          ? async () => {
                              await remove({ data: { id: m.id } });
                              toast.success(t("app.pages.settings.memberRemoved"));
                              loadTeam();
                            }
                          : undefined
                      }
                    />
                  ))}
                  {team.members.length === 0 && team.role === "owner" && (
                    <div className="px-4 py-6 text-center text-xs text-muted-foreground">
                      {t("app.pages.settings.noTeammates")}
                    </div>
                  )}
                </div>

                {(team.invites ?? []).length > 0 && (
                  <div className="mt-5">
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono mb-2">
                      {t("app.pages.settings.pendingInvites")}
                    </div>
                    <div className="rounded-xl border border-dashed border-border overflow-hidden divide-y divide-border">
                      {team.invites.map((i: any) => (
                        <div
                          key={i.id}
                          className="flex items-center justify-between gap-3 px-4 py-3"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="h-9 w-9 shrink-0 rounded-full border border-dashed border-border flex items-center justify-center text-muted-foreground">
                              <Send className="h-3.5 w-3.5" />
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm truncate">{i.email}</div>
                              <div className="text-[11px] text-muted-foreground">
                                {i.role} ·{" "}
                                {t("app.pages.settings.invited", {
                                  date: new Date(i.created_at).toLocaleDateString(),
                                })}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={async () => {
                              await revoke({ data: { id: i.id } });
                              loadTeam();
                            }}
                            className="shrink-0 text-muted-foreground hover:text-red-400 transition"
                            title={t("app.pages.settings.revokeTitle")}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {team.role === "owner" && (
                  <form
                    onSubmit={sendInvite}
                    className="mt-5 pt-5 border-t border-border flex flex-wrap gap-2"
                  >
                    <input
                      type="email"
                      required
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder={t("app.pages.settings.emailPh")}
                      className={`${inp} flex-1 min-w-52`}
                    />
                    <select
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value as any)}
                      className={`${inp} w-32`}
                    >
                      <option value="member">{t("app.pages.settings.roleMember")}</option>
                      <option value="admin">{t("app.pages.settings.roleAdmin")}</option>
                    </select>
                    <button
                      type="submit"
                      disabled={inviting || !inviteEmail.trim()}
                      className="rounded-full brand-gradient text-black px-5 py-2 text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
                    >
                      <UserPlus className="h-3.5 w-3.5" />{" "}
                      {inviting
                        ? t("app.pages.settings.sending")
                        : t("app.pages.settings.sendInvite")}
                    </button>
                  </form>
                )}
              </>
            )}
          </div>
        </div>
      )}
      {tab === "integrations" && (
        <div className="space-y-4 max-w-2xl">
          <NuvatrSection />
          <div className="glass-strong rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-1">
              <Webhook className="h-4 w-4 text-[oklch(0.68_0.19_290)]" />
              <div className="font-display font-semibold">
                {t("app.pages.settings.leadWebhook")}
              </div>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              {t("app.pages.settings.webhookDescBefore")}
              <code className="px-1 rounded bg-white/5">X-RTN-Signature</code>
              {t("app.pages.settings.webhookDescAfter")}
            </p>
            {!integrations ? (
              <div className="text-sm text-muted-foreground py-4">
                {t("app.pages.common.loading")}
              </div>
            ) : (
              <div className="space-y-4">
                <Field label={t("app.pages.settings.webhookUrl")}>
                  <input
                    value={webhookUrl}
                    onChange={(e) => setWebhookUrl(e.target.value)}
                    placeholder="https://hooks.zapier.com/…"
                    className={inp}
                  />
                </Field>
                {integrations.webhookSecret && (
                  <Field label={t("app.pages.settings.signingSecret")}>
                    <div className="flex gap-2">
                      <input
                        readOnly
                        value={integrations.webhookSecret}
                        className={`${inp} font-mono text-xs`}
                      />
                      <button
                        type="button"
                        title={t("app.pages.settings.copySecretTitle")}
                        onClick={() =>
                          navigator.clipboard
                            .writeText(integrations.webhookSecret)
                            .then(() => toast.success(t("app.pages.settings.secretCopied")))
                        }
                        className="rounded-xl glass border border-border px-3 hover:bg-white/5"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        title={t("app.pages.settings.genSecretTitle")}
                        disabled={savingHook}
                        onClick={() => submitWebhook(true)}
                        className="rounded-xl glass border border-border px-3 hover:bg-white/5 disabled:opacity-50"
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </Field>
                )}
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => submitWebhook(false)}
                    disabled={savingHook}
                    className="rounded-full brand-gradient text-black px-5 py-2 text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
                  >
                    <Save className="h-3.5 w-3.5" />{" "}
                    {savingHook
                      ? t("app.pages.common.saving")
                      : t("app.pages.settings.saveWebhook")}
                  </button>
                  <button
                    onClick={runTestWebhook}
                    disabled={testingHook || !integrations.webhookUrl}
                    className="rounded-full glass border border-border px-5 py-2 text-sm hover:bg-white/5 disabled:opacity-50 flex items-center gap-1.5"
                  >
                    <Send className="h-3.5 w-3.5" />{" "}
                    {testingHook
                      ? t("app.pages.settings.sending")
                      : t("app.pages.settings.sendTest")}
                  </button>
                  {integrations.webhookUrl && (
                    <button
                      onClick={() => {
                        setWebhookUrl("");
                        submitWebhook(false);
                      }}
                      disabled={savingHook}
                      className="rounded-full glass border border-border px-5 py-2 text-sm text-muted-foreground hover:bg-white/5 disabled:opacity-50"
                    >
                      {t("app.pages.settings.disable")}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {tab === "notifications" &&
        (prefs ? (
          <form onSubmit={submitPrefs} className="glass-strong rounded-2xl p-6 space-y-2 max-w-lg">
            <div className="flex items-start gap-3 mb-4">
              <div className="h-10 w-10 shrink-0 rounded-xl bg-[oklch(0.82_0.18_168_/_0.12)] border border-[oklch(0.82_0.18_168_/_0.25)] flex items-center justify-center">
                <Bell className="h-4 w-4 text-[oklch(0.82_0.18_168)]" />
              </div>
              <div>
                <div className="font-display text-lg font-semibold">
                  {t("app.pages.settings.emailNotifs")}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {t("app.pages.settings.emailNotifsSub")}
                </p>
              </div>
            </div>
            <div className="rounded-xl border border-border overflow-hidden divide-y divide-border">
              {(
                [
                  [
                    "email_leads",
                    t("app.pages.settings.leadAlerts"),
                    t("app.pages.settings.leadAlertsSub"),
                  ],
                  [
                    "email_weekly",
                    t("app.pages.settings.weeklyDigest"),
                    t("app.pages.settings.weeklyDigestSub"),
                  ],
                  [
                    "email_product",
                    t("app.pages.settings.productUpdates"),
                    t("app.pages.settings.productUpdatesSub"),
                  ],
                ] as const
              ).map(([k, title, sub]) => (
                <label
                  key={k}
                  className="flex items-center justify-between gap-3 px-4 py-3.5 cursor-pointer hover:bg-white/[0.02] transition"
                >
                  <div>
                    <div className="text-sm font-medium">{title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={!!prefs[k]}
                    onChange={(e) => setPrefs({ ...prefs, [k]: e.target.checked })}
                    className="h-4 w-4 accent-[oklch(0.82_0.18_168)] shrink-0"
                  />
                </label>
              ))}
            </div>
            <button
              type="submit"
              className="mt-2 rounded-full brand-gradient text-black px-5 py-2 text-sm font-medium flex items-center gap-1.5"
            >
              <Save className="h-3.5 w-3.5" /> {t("app.pages.settings.savePrefs")}
            </button>
          </form>
        ) : (
          <div className="p-6 text-muted-foreground">{t("app.pages.common.loading")}</div>
        ))}
      {tab === "security" && (
        <div className="space-y-4 max-w-lg">
          <form onSubmit={updatePassword} className="glass-strong rounded-2xl p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 shrink-0 rounded-xl bg-[oklch(0.82_0.18_168_/_0.12)] border border-[oklch(0.82_0.18_168_/_0.25)] flex items-center justify-center">
                <Lock className="h-4 w-4 text-[oklch(0.82_0.18_168)]" />
              </div>
              <div>
                <div className="font-display text-lg font-semibold">
                  {t("app.pages.settings.passwordTitle")}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {t("app.pages.settings.passwordSub")}
                </p>
              </div>
            </div>
            <Field label={t("app.pages.settings.newPassword")}>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inp}
                placeholder={t("app.pages.settings.passwordPh")}
              />
            </Field>
            <button
              type="submit"
              className="rounded-full brand-gradient text-black px-5 py-2 text-sm font-medium flex items-center gap-1.5"
            >
              <Save className="h-3.5 w-3.5" /> {t("app.pages.settings.updatePassword")}
            </button>
          </form>
          <div className="glass-strong rounded-2xl p-6">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 shrink-0 rounded-xl bg-white/5 border border-border flex items-center justify-center">
                <Shield className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium">{t("app.pages.settings.activeSessions")}</div>
                <div className="text-xs text-muted-foreground mt-1 mb-3">
                  {t("app.pages.settings.activeSessionsSub")}
                </div>
                <button
                  onClick={signOutAll}
                  className="rounded-full border border-border px-5 py-2 text-sm hover:bg-white/5 transition"
                >
                  {t("app.pages.settings.signOutAll")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}

const inp =
  "w-full rounded-xl bg-white/5 border border-border px-4 py-2.5 text-sm focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]";
function Field({ label, children }: any) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono mb-1.5">
        {label}
      </div>
      {children}
    </label>
  );
}

// Deterministic accent hue per member so avatars feel personal but stay on-brand.
function avatarHue(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) % 360;
  return h;
}

function RoleBadge({ role }: { role: string }) {
  const t = useHydratedTranslation();
  if (role === "owner") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide px-2.5 py-1 rounded-full bg-[oklch(0.82_0.18_168_/_0.12)] text-[oklch(0.82_0.18_168)] border border-[oklch(0.82_0.18_168_/_0.25)]">
        <Crown className="h-3 w-3" /> {t("app.pages.settings.roleOwner")}
      </span>
    );
  }
  if (role === "admin") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide px-2.5 py-1 rounded-full bg-[oklch(0.68_0.19_290_/_0.12)] text-[oklch(0.74_0.16_290)] border border-[oklch(0.68_0.19_290_/_0.25)]">
        <ShieldCheck className="h-3 w-3" /> {t("app.pages.settings.roleAdmin")}
      </span>
    );
  }
  return (
    <span className="text-[11px] font-medium uppercase tracking-wide px-2.5 py-1 rounded-full bg-white/5 border border-border text-muted-foreground">
      {t("app.pages.settings.roleMember")}
    </span>
  );
}

function MemberRow({
  email,
  role,
  isYou = false,
  onRemove,
}: {
  email: string;
  role: string;
  isYou?: boolean;
  onRemove?: () => void | Promise<void>;
}) {
  const t = useHydratedTranslation();
  const hue = avatarHue(email);
  const initial = (email.trim()[0] ?? "?").toUpperCase();
  return (
    <div
      className={`flex items-center justify-between gap-3 px-4 py-3 ${isYou ? "bg-white/[0.03]" : ""}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          className="h-9 w-9 shrink-0 rounded-full flex items-center justify-center text-[13px] font-semibold text-black"
          style={{ background: `oklch(0.82 0.15 ${hue})` }}
        >
          {initial}
        </div>
        <div className="min-w-0">
          <div className="text-sm truncate font-medium">{email}</div>
          {isYou && (
            <div className="text-[11px] text-muted-foreground">
              {t("app.pages.settings.workspaceOwner")}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2.5 shrink-0">
        <RoleBadge role={role} />
        {onRemove && (
          <button
            onClick={onRemove}
            className="text-muted-foreground hover:text-red-400 transition"
            title={t("app.pages.settings.removeMemberTitle")}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
